import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  Loader2, Wallet, Lock, Eye, EyeOff, Check, X, AlertTriangle,
  CheckCircle2, ShieldCheck, ArrowLeft, ArrowRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api } from "../lib/api";

/**
 * JMS ERP · ResetPassword — Consome token de e-mail e define nova senha.
 * Fases: (1) validating · (2) form · (3) invalid · (4) success
 */
export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [phase, setPhase] = useState("validating"); // validating | form | invalid | success
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Validate token on mount
  useEffect(() => {
    let cancelled = false;
    if (!token || token.length < 16) {
      setPhase("invalid");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/auth/reset-password/validate?token=${encodeURIComponent(token)}`);
        if (!cancelled) setPhase(data?.valid ? "form" : "invalid");
      } catch {
        if (!cancelled) setPhase("invalid");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Password strength checks
  const checks = useMemo(() => ({
    length:  pwd.length >= 8,
    upper:   /[A-Z]/.test(pwd),
    lower:   /[a-z]/.test(pwd),
    digit:   /\d/.test(pwd),
    special: /[^A-Za-z0-9]/.test(pwd),
    match:   pwd.length > 0 && pwd === confirm,
  }), [pwd, confirm]);

  const allChecksPass = checks.length && checks.upper && checks.lower && checks.digit && checks.special && checks.match;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!allChecksPass || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setPhase("success");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) {
        setErrorMsg(err?.response?.data?.detail || "Link inválido ou expirado. Solicite uma nova recuperação.");
        // If token became invalid, kick user to invalid phase after showing error briefly
      } else {
        setErrorMsg("Não foi possível redefinir sua senha agora. Tente novamente em instantes.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="relative min-h-screen w-full overflow-hidden bg-[#F8FAFC] flex items-center justify-center px-4 py-8 sm:px-6"
    >
      {/* Background */}
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(22,163,74,0.08),transparent_65%)]" />
        <div className="absolute -top-32 -left-24 h-[360px] w-[360px] rounded-full bg-primary/15 blur-3xl opacity-70" />
        <div className="absolute top-1/3 -right-32 h-[420px] w-[420px] rounded-full bg-emerald-200/40 blur-3xl opacity-60" />
        <div className="absolute -bottom-32 left-1/3 h-[320px] w-[320px] rounded-full bg-primary/10 blur-3xl opacity-70" />
      </div>

      <div className="relative z-10 w-full max-w-[480px] fade-up">
        <div
          data-testid="reset-password-card"
          className="bg-white rounded-[20px] shadow-[0_20px_60px_-15px_rgba(15,23,42,0.15),0_8px_20px_-8px_rgba(15,23,42,0.08)] ring-1 ring-slate-200/60 p-10"
        >
          {/* Header — logo always visible */}
          <div className="flex flex-col items-center text-center mb-6">
            <div
              aria-hidden="true"
              className="h-14 w-14 rounded-2xl grid place-items-center bg-gradient-to-br from-primary to-emerald-600 shadow-[0_10px_25px_-8px_rgba(22,163,74,0.55)] mb-4"
            >
              <Wallet className="h-7 w-7 text-white" strokeWidth={2.2} />
            </div>
            <h1 className="font-display font-bold text-2xl tracking-tight text-slate-900">JMS ERP</h1>
          </div>

          {phase === "validating" && (
            <div data-testid="reset-validating" className="py-8 flex flex-col items-center gap-3">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-sm text-slate-500">Validando link de recuperação...</p>
            </div>
          )}

          {phase === "invalid" && (
            <div data-testid="reset-invalid" className="space-y-5">
              <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
                <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
                <div className="leading-snug">
                  <strong className="block font-semibold mb-0.5">Link expirado ou inválido</strong>
                  <span>Este link de recuperação expirou ou é inválido.</span>
                </div>
              </div>
              <Button
                type="button"
                data-testid="reset-back-to-login-button"
                onClick={() => navigate("/login")}
                className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] transition-all duration-200"
              >
                <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                Voltar ao Login
              </Button>
              <div className="text-center">
                <Link to="/forgot-password" data-testid="reset-request-new-link" className="text-sm text-primary hover:underline underline-offset-4">
                  Solicitar novo link de recuperação
                </Link>
              </div>
            </div>
          )}

          {phase === "form" && (
            <div>
              <div className="text-center mb-6">
                <h2 className="font-display text-[22px] font-semibold text-slate-900 tracking-tight">
                  Redefinir Senha
                </h2>
                <p className="mt-2 text-sm text-slate-500 max-w-[360px] mx-auto">
                  Escolha uma nova senha segura para sua conta JMS ERP.
                </p>
              </div>
              <form onSubmit={onSubmit} className="space-y-5" data-testid="reset-form">
                {/* New password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">Nova senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
                    <Input
                      id="new-password"
                      data-testid="reset-new-password-input"
                      type={showPwd ? "text" : "password"}
                      required
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Digite sua nova senha"
                      aria-label="Nova senha"
                      className="h-[52px] pl-11 pr-12 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      data-testid="reset-new-password-toggle"
                      aria-label={showPwd ? "Ocultar senha" : "Mostrar senha"}
                      aria-pressed={showPwd}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
                    >
                      {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Confirm */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">Confirmar nova senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden="true" />
                    <Input
                      id="confirm-password"
                      data-testid="reset-confirm-password-input"
                      type={showConfirm ? "text" : "password"}
                      required
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      autoComplete="new-password"
                      placeholder="Repita sua nova senha"
                      aria-label="Confirmar nova senha"
                      className="h-[52px] pl-11 pr-12 rounded-xl border-slate-200 bg-slate-50/60 text-slate-900 placeholder:text-slate-400 focus-visible:bg-white focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 transition-all duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      data-testid="reset-confirm-password-toggle"
                      aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
                      aria-pressed={showConfirm}
                      className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
                    >
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Requirements */}
                <ul data-testid="reset-checks" className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {[
                    { key: "length",  label: "Mínimo 8 caracteres" },
                    { key: "upper",   label: "Uma letra maiúscula" },
                    { key: "lower",   label: "Uma letra minúscula" },
                    { key: "digit",   label: "Um número" },
                    { key: "special", label: "Um caractere especial" },
                    { key: "match",   label: "Senhas coincidem" },
                  ].map((c) => {
                    const ok = checks[c.key];
                    return (
                      <li key={c.key} data-testid={`reset-check-${c.key}`} data-passed={ok ? "true" : "false"} className={`flex items-center gap-2 ${ok ? "text-emerald-700" : "text-slate-400"}`}>
                        {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{c.label}</span>
                      </li>
                    );
                  })}
                </ul>

                {errorMsg && (
                  <div role="alert" data-testid="reset-error" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-red-500" aria-hidden="true" />
                    <span>{errorMsg}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  data-testid="reset-submit-button"
                  disabled={!allChecksPass || submitting}
                  className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] tracking-tight shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] hover:shadow-[0_14px_30px_-10px_rgba(22,163,74,0.7)] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)]"
                >
                  {submitting ? (
                    <span className="inline-flex items-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Redefinindo...
                    </span>
                  ) : (
                    "Redefinir Senha"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  data-testid="reset-cancel-button"
                  onClick={() => navigate("/login")}
                  className="w-full h-11 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                  Cancelar e voltar ao Login
                </Button>
              </form>
            </div>
          )}

          {phase === "success" && (
            <div data-testid="reset-success" className="space-y-5">
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm text-emerald-800">
                <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-emerald-600" aria-hidden="true" />
                <div className="leading-snug">
                  <strong className="block font-semibold mb-0.5" data-testid="reset-success-title">
                    Senha alterada com sucesso.
                  </strong>
                  <span>Sua senha foi atualizada. Faça login com as novas credenciais.</span>
                </div>
              </div>
              <Button
                type="button"
                data-testid="reset-go-to-login-button"
                onClick={() => navigate("/login", { replace: true })}
                className="w-full h-[54px] rounded-xl bg-primary hover:bg-primary/90 active:scale-[0.985] text-primary-foreground font-semibold text-[15px] shadow-[0_10px_24px_-10px_rgba(22,163,74,0.6)] transition-all duration-200"
              >
                Ir para Login
                <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
              </Button>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-slate-100 flex flex-col items-center text-center gap-1">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              Ambiente Seguro
            </div>
            <p className="text-[11px] text-slate-400">© 2026 JMS MULTI SERVICE LTDA</p>
            <p className="text-[11px] text-slate-400">Versão 1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
